/**
 * LinkifiedText — renders a plain string, turning any http(s) URLs it contains
 * into clickable links that open in a new tab.
 *
 * Used by the intelligence-agent finding cards (notably Beacon, whose external
 * signals embed a source URL inside the finding detail). The visible label is
 * the URL's hostname (the article title/source already precedes it in the
 * text), with an external-link affordance; the full URL is the href + title.
 */

import { ExternalLink } from 'lucide-react';

// Capturing group so String.split keeps the URL as its own segment.
const URL_RE = /(https?:\/\/[^\s<]+)/g;

function hostLabel(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url.length > 48 ? `${url.slice(0, 45)}…` : url;
  }
}

interface LinkifiedTextProps {
  text: string;
  className?: string;
}

export function LinkifiedText({ text, className }: LinkifiedTextProps) {
  const parts = text.split(URL_RE);

  return (
    <p className={className}>
      {parts.map((part, i) =>
        /^https?:\/\//i.test(part) ? (
          <a
            key={i}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            title={part}
            className="inline-flex items-center gap-0.5 text-primary-600 hover:text-primary-800 hover:underline break-all"
          >
            {hostLabel(part)}
            <ExternalLink className="h-3 w-3 flex-shrink-0" />
          </a>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </p>
  );
}

export default LinkifiedText;
